import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Download, CheckCircle2, Clock, Plus, X } from "lucide-react";
import { adminApi } from "../../api/auth";
import { Input } from "../../components/ui/input";

const MET = { paypal: "PayPal", mp_ar: "MP-AR", mp_cl: "MP-CL",
  khipu: "Khipu CL", khipu_cl: "Khipu CL", khipu_ar: "Khipu AR",
  crypto: "USDT", manual: "Manual" };
const fmtDate = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString("es-AR") : "—";

export default function AdminClaims() {
  const [filters, setFilters] = useState({ status: "all", method: "", q: "", page: 1, size: 50 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true); setErr("");
    try { setData(await adminApi.claims(filters)); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));

  return (
    <div className="space-y-4" data-testid="admin-claims">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="ornament"><span>Claims</span></div>
          <h1 className="section-title mt-3 text-3xl">Reclamos en juego</h1>
        </div>
        <div className="flex gap-2">
          <ManualClaimButton onCreated={fetchData} />
          <a href={adminApi.exportUrl("claims", filters)}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider bg-[#d4a843] text-black rounded hover:bg-[#e0b860]"
            data-testid="export-claims-csv">
            <Download className="w-3.5 h-3.5" /> CSV
          </a>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); fetchData(); }}
        className="border border-[#3a3328] bg-[#15110a]/70 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select value={filters.status} onChange={(e) => set("status", e.target.value)}
            className="bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5">
            <option value="all">Todos</option>
            <option value="claimed">Reclamados</option>
            <option value="unclaimed">Pendientes</option>
          </select>
          <select value={filters.method} onChange={(e) => set("method", e.target.value)}
            className="bg-[#1a1610] border border-[#3a3328] text-white text-sm rounded px-2 py-1.5">
            <option value="">Todos metodos</option>
            <option value="paypal">PayPal</option>
            <option value="mp_ar">MercadoPago AR</option>
            <option value="mp_cl">MercadoPago CL</option>
            <option value="khipu_cl">Khipu CL</option>
            <option value="khipu_ar">Khipu AR</option>
            <option value="manual">Manual</option>
          </select>
          <Input placeholder="buscar txn, email, character..." value={filters.q} onChange={(e) => set("q", e.target.value)}
            className="bg-[#1a1610] border-[#3a3328] text-white text-sm h-9 md:col-span-2" />
        </div>
      </form>

      {err && <div className="text-red-400 text-sm border border-red-900/40 p-3 rounded">{err}</div>}

      <div className="border border-[#3a3328] bg-[#15110a]/70 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase text-[#7e7768] border-b border-[#3a3328] bg-black/30">
              <th className="text-left px-3 py-2">Donado</th>
              <th className="text-left">TXN</th>
              <th className="text-left">Metodo</th>
              <th className="text-left">Email</th>
              <th className="text-right">Coins</th>
              <th>Status</th>
              <th className="text-left">Reclamado por</th>
              <th className="text-left">Fecha claim</th>
              <th className="text-left">IP claim</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#d4a843] inline" /></td></tr>}
              {!loading && data?.rows?.length === 0 && <tr><td colSpan={9} className="text-center text-[#7e7768] py-6">Sin resultados</td></tr>}
              {!loading && data?.rows?.map((c) => (
                <tr key={c.id} className="border-b border-[#3a3328]/30">
                  <td className="px-3 py-2 text-xs text-[#b8b3a8] whitespace-nowrap">{fmtDate(c.donated_time)}</td>
                  <td className="font-mono text-xs text-[#b8b3a8] max-w-[120px] truncate" title={c.txn_id}>{c.txn_id}</td>
                  <td className="text-[#b8b3a8]">{MET[c.method] || c.method}</td>
                  <td className="text-[#b8b3a8] text-xs">{c.email}</td>
                  <td className="text-right text-amber-300">{Number(c.coins).toLocaleString()}</td>
                  <td className="text-center">
                    {c.claimed === 1
                      ? <span className="inline-flex items-center gap-1 text-green-400 text-xs"><CheckCircle2 className="w-3 h-3" /> OK</span>
                      : <span className="inline-flex items-center gap-1 text-amber-400 text-xs"><Clock className="w-3 h-3" /> pendiente</span>}
                  </td>
                  <td className="text-white text-xs">
                    {c.claimed_character || "—"}
                    {c.claimed_account && <span className="text-[#7e7768]"> ({c.claimed_account})</span>}
                  </td>
                  <td className="text-xs text-[#b8b3a8] whitespace-nowrap">{fmtDate(c.claimed_time)}</td>
                  <td className="text-xs text-[#b8b3a8]">{c.claimed_ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 text-xs border-t border-[#3a3328]">
            <span className="text-[#7e7768]">{data.total} claims · pagina {data.page} de {data.pages}</span>
            <div className="flex gap-1">
              <button onClick={() => set("page", Math.max(1, filters.page - 1))} disabled={filters.page <= 1}
                className="px-2 py-1 border border-[#3a3328] rounded text-[#b8b3a8] disabled:opacity-40">‹</button>
              <button onClick={() => set("page", Math.min(data.pages, filters.page + 1))} disabled={filters.page >= data.pages}
                className="px-2 py-1 border border-[#3a3328] rounded text-[#b8b3a8] disabled:opacity-40">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ManualClaimButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ account: "", coins: "", email: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.account || !form.coins || !form.reason) {
      setErr("Account, coins y razon son obligatorios"); return;
    }
    if (!confirm(`Crear claim manual de ${form.coins} coins para "${form.account}"?`)) return;
    setLoading(true);
    try {
      const res = await adminApi.manualClaim({
        account: form.account.trim().toLowerCase(),
        coins: parseInt(form.coins, 10),
        email: form.email.trim(),
        reason: form.reason.trim(),
      });
      alert(`Claim creado: ${res.txn_id}. ${res.message}`);
      setForm({ account: "", coins: "", email: "", reason: "" });
      setOpen(false);
      onCreated && onCreated();
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        data-testid="open-manual-claim"
        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider bg-amber-700 text-white rounded hover:bg-amber-600">
        <Plus className="w-3.5 h-3.5" /> Crear claim manual
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="bg-[#15110a] border border-[#3a3328] rounded-lg max-w-md w-full p-6 relative">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-[#7e7768] hover:text-white">
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-lg text-white mb-1 flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-400" /> Crear claim manual
            </h3>
            <p className="text-xs text-[#b8b3a8] mb-4">Acredita coins a una cuenta sin pago. Util para premios, compensaciones, transferencias por fuera.</p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#d4a843] block mb-1">Account L2</label>
                <Input type="text" value={form.account} onChange={(e) => setForm((f) => ({ ...f, account: e.target.value }))}
                  placeholder="ej: alone" required data-testid="manual-claim-account"
                  className="bg-[#0a0a0d] border-[#3a3328] text-white h-9" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#d4a843] block mb-1">Coins a acreditar</label>
                <Input type="number" min="1" max="1000000" value={form.coins} onChange={(e) => setForm((f) => ({ ...f, coins: e.target.value }))}
                  required data-testid="manual-claim-coins"
                  className="bg-[#0a0a0d] border-[#3a3328] text-white h-9" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#d4a843] block mb-1">Email (opcional)</label>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Usa el de la cuenta si lo dejas vacio"
                  className="bg-[#0a0a0d] border-[#3a3328] text-white h-9" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#d4a843] block mb-1">Razon (obligatorio, queda en audit)</label>
                <textarea rows={3} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  required data-testid="manual-claim-reason"
                  placeholder="ej: Premio del evento del sabado / Compensacion por bug / Transferencia bancaria por fuera"
                  className="w-full bg-[#0a0a0d] border border-[#3a3328] text-white text-sm rounded p-2" />
              </div>
              {err && <div className="text-red-400 text-xs">{err}</div>}
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={loading}
                  data-testid="manual-claim-submit"
                  className="flex-1 px-4 py-2 bg-amber-600 text-black text-sm rounded font-medium hover:bg-amber-500 disabled:opacity-50">
                  {loading ? "Creando..." : "Crear claim"}
                </button>
                <button type="button" onClick={() => setOpen(false)}
                  className="px-4 py-2 border border-[#3a3328] text-[#b8b3a8] text-sm rounded hover:bg-white/5">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
